/* Peek carousel — marks the centred card, drives the arrows, and loops.

   SCROLLING ITSELF IS THE BROWSER'S. The rail is an overflow container with
   scroll-snap, so swipe, trackpad, shift-wheel and keyboard all work whether or
   not this file loads. Everything here is presentation: which card is scaled
   up, whether the arrows are live, and the looping.

   THE LOOP IS THREE COPIES AND A SILENT JUMP, not a rewritten scroller. The
   card list is cloned once before and once after the real one, and whenever the
   middle of the viewport leaves the middle copy the rail's scrollLeft is moved
   by exactly one copy's width. Position within the copy is unchanged, so
   nothing moves on screen — the shopper just never reaches an end. Native
   scrolling, snapping and momentum all keep working, which is the point: an
   index-driven transform carousel would have had to reimplement every one of
   them.

   The jump is a direct scrollLeft assignment, never a smooth scroll. A smooth
   jump would animate across the whole copy and be plainly visible.

   THE JUMP WAITS FOR THE RAIL TO STOP. It used to run on every scroll event,
   which is what made the wrap from the last card to the first visibly stutter:
   a smooth scroll started by an arrow is animating towards a fixed position,
   and moving scrollLeft by a whole copy underneath it leaves the animation
   heading for a target that is now one copy away from where it should be. The
   rail lurched to catch up.

   Deferring costs nothing, because the card the shopper is looking at during
   the delay is a CLONE OF THE ONE THEY ARE SCROLLING TO — identical markup,
   identical photo. The jump then happens once the rail is quiet, from a
   position inside the copy to the same position in the next, and is invisible.
   The only thing deferring risks is running out of rail, and that is covered
   below: half a copy past the boundary the jump happens anyway, animation or
   not, where there is still a copy and a half of track in hand. */
(function () {
  'use strict';

  function init(frame) {
    if (!frame || frame.dataset.spcReady === '1') return;
    var rail = frame.querySelector('[data-spc-rail]');
    if (!rail) return;
    frame.dataset.spcReady = '1';

    var originals = Array.prototype.slice.call(rail.querySelectorAll('[data-spc-card]'));
    if (!originals.length) return;

    var navs = Array.prototype.slice.call(frame.querySelectorAll('[data-spc-nav]'));
    var timer = 0;

    /* Looping needs enough cards that a whole copy is wider than the rail;
       below that the jump would land mid-viewport and be visible. */
    var looping = frame.dataset.loop === 'true' && originals.length >= 4;
    var count = originals.length;

    if (looping) {
      var tail = document.createDocumentFragment();
      var head = document.createDocumentFragment();
      originals.forEach(function (card) {
        var after = card.cloneNode(true);
        after.dataset.spcClone = '1';
        tail.appendChild(after);
      });
      originals.forEach(function (card) {
        var before = card.cloneNode(true);
        before.dataset.spcClone = '1';
        head.appendChild(before);
      });
      rail.appendChild(tail);
      rail.insertBefore(head, rail.firstChild);
    }

    var cards = Array.prototype.slice.call(rail.querySelectorAll('[data-spc-card]'));

    /* One copy's width, measured from the live layout so the negative overlap
       margins are already accounted for. */
    function blockWidth() {
      if (!looping) return 0;
      return cards[count].offsetLeft - cards[0].offsetLeft;
    }

    /* How far the middle of the viewport sits outside the middle copy, in
       copies. 0 while inside it; 1 exactly one copy past either edge. */
    function drift() {
      if (!looping) return 0;
      var block = blockWidth();
      if (block <= 0) return 0;
      var mid = rail.scrollLeft + rail.clientWidth / 2;
      var start = cards[count].offsetLeft;
      if (mid < start) return (start - mid) / block;
      if (mid >= start + block) return (mid - start - block) / block;
      return 0;
    }

    function normalise() {
      if (!looping) return;
      var block = blockWidth();
      if (block <= 0) return;

      var mid = rail.scrollLeft + rail.clientWidth / 2;
      var start = cards[count].offsetLeft;

      if (mid < start) rail.scrollLeft += block;
      else if (mid >= start + block) rail.scrollLeft -= block;
    }

    function step() {
      var first = cards[0];
      if (!first) return rail.clientWidth * 0.8;
      var styles = getComputedStyle(first);
      var overlap = parseFloat(styles.marginInlineStart) || 0;
      return first.getBoundingClientRect().width + overlap;
    }

    /* Paint only — which card is centred, how the rings grade, whether the
       arrows are live. Deliberately does NOT move the rail: see the note at the
       top about why the jump waits. The one exception is the safety valve, for
       a scroll long enough that waiting would run off the end of the track. */
    function sync() {
      if (drift() > 0.5) normalise();

      var mid = rail.scrollLeft + rail.clientWidth / 2;
      var best = null;
      var bestDistance = Infinity;

      cards.forEach(function (card) {
        var centre = card.offsetLeft + card.offsetWidth / 2;
        var distance = Math.abs(centre - mid);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = card;
        }
      });

      /* Tag each card with how far it sits from the middle so the CSS can step
         the rings down in size and stacking order. Capped at 2 — see the
         stylesheet for why a third ring would re-open the gaps. */
      var centre = cards.indexOf(best);
      cards.forEach(function (card, i) {
        card.classList.toggle('is-active', card === best);
        var depth = String(Math.min(2, Math.abs(i - centre)));
        if (card.dataset.spcDepth !== depth) card.dataset.spcDepth = depth;
      });

      if (looping) {
        navs.forEach(function (nav) {
          nav.disabled = false;
        });
      } else {
        var max = rail.scrollWidth - rail.clientWidth;
        navs.forEach(function (nav) {
          var dir = Number(nav.getAttribute('data-spc-nav'));
          nav.disabled = dir < 0 ? rail.scrollLeft <= 2 : rail.scrollLeft >= max - 2;
        });
      }

      /* Marking the rail ready is what releases the CSS that dims the
         neighbours; until then every card renders at full size, so a failed
         script leaves a plain scroller rather than a row of shrunken cards. */
      rail.dataset.spcReady = '1';
    }

    /* The rail has stopped: safe to move it back into the middle copy. */
    function settle() {
      normalise();
      sync();
    }

    function scrollBy(delta) {
      var from = rail.scrollLeft;
      var target = from + delta;

      if (!looping) {
        var max = rail.scrollWidth - rail.clientWidth;
        target = Math.max(0, Math.min(max, target));
      }
      if (target === from) return;

      rail.scrollTo({ left: target, behavior: 'smooth' });
      /* The fallback only fires when the smooth scroll did not happen at all.
         Checking against the START position rather than the target is what
         makes that distinction: a scroll in progress has already moved. */
      setTimeout(function () {
        if (Math.abs(rail.scrollLeft - from) < 2) rail.scrollLeft = target;
        settle();
      }, 420);
    }

    navs.forEach(function (nav) {
      nav.addEventListener('click', function () {
        scrollBy(Number(nav.getAttribute('data-spc-nav')) * step());
      });
    });

    /* SYNC DURING THE SCROLL, NOT AFTER IT. This used to wait 60ms of quiet
       before swapping the active card, which meant the scale change only
       started once the rail had already stopped — the swap read as a jolt
       tacked onto the end of the movement rather than part of it. Now it runs
       on every frame of the scroll, so the card is already growing as it
       arrives in the middle.

       Coalesced to one pass per frame, with a timeout as the backstop:
       requestAnimationFrame is throttled in background and automated contexts,
       and a carousel whose active card never updates there is one that looks
       broken in exactly the places it gets screenshotted. The timeout is also
       what tells us the rail has stopped, which is when the loop jump runs. */
    var framed = false;
    function onScroll() {
      if (!framed) {
        framed = true;
        requestAnimationFrame(function () {
          framed = false;
          sync();
        });
      }
      clearTimeout(timer);
      timer = setTimeout(settle, 140);
    }

    rail.addEventListener('scroll', onScroll, { passive: true });

    if (window.ResizeObserver) new ResizeObserver(settle).observe(rail);
    else window.addEventListener('resize', settle);

    /* Open inside the middle copy when looping, so there is room to scroll both
       ways before the first jump; otherwise on the middle card, because the
       peek only reads as a peek with something on both sides. */
    var opener = looping ? cards[count] : cards[Math.floor(cards.length / 2)];
    if (opener && cards.length > 2) {
      rail.scrollLeft = opener.offsetLeft + opener.offsetWidth / 2 - rail.clientWidth / 2;
    }

    sync();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-spc]'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target.querySelector('[data-spc]'));
  });
})();
